/**
 * Consistent page header across Queue, Ledger, Tax AI, Jobs, Export, Settings.
 * Left: optional back chevron + orange-glowing icon square + title + subtitle.
 * Right (optional): action slot for filter chips, buttons, etc.
 *
 * `backTo` renders a 44×44 chevron button to the LEFT of the icon. Tapping it
 * calls `navigate(backTo)`. Callers that need a custom onBack (rare) can
 * pass `onBack` instead. Both are optional.
 */
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PageHeader({
  icon: Icon,
  title,
  subtitle,
  testid,
  right,
  accent = true,
  backTo,
  onBack,
}) {
  const navigate = useNavigate();
  const showBack = !!(backTo || onBack);
  const handleBack = () => {
    if (onBack) return onBack();
    if (backTo) navigate(backTo);
  };
  return (
    <header
      className="flex items-start justify-between gap-3 pt-1"
      data-testid={testid || "page-header"}
    >
      <div className="flex items-center gap-2 min-w-0">
        {showBack && (
          <button
            type="button"
            onClick={handleBack}
            data-testid={`${testid || "page-header"}-back`}
            aria-label="Back"
            className="w-11 h-11 -ml-2 shrink-0 rounded-xl flex items-center justify-center text-white/80 hover:bg-white/5 active:scale-95 transition"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={2.4} />
          </button>
        )}
        {Icon && (
          <div
            className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${
              accent
                ? "bg-gradient-to-br from-[#FF9500] to-[#E68600] text-white shadow-[0_6px_16px_-6px_rgba(255,149,0,0.65)]"
                : "bg-white/5 border border-white/10 text-[#FF9500]"
            }`}
          >
            <Icon className="w-6 h-6" strokeWidth={2.4} />
            {accent && (
              <span className="absolute pointer-events-none inline-block w-12 h-12 rounded-2xl ring-1 ring-inset ring-white/25" />
            )}
          </div>
        )}
        <div className="min-w-0">
          <h1 className="font-heading text-[26px] sm:text-3xl font-extrabold leading-tight tracking-tight truncate">
            {title}
          </h1>
          {subtitle && (
            <div className="text-[13px] text-white/55 mt-0.5 truncate">{subtitle}</div>
          )}
        </div>
      </div>
      {right && <div className="shrink-0 flex items-center gap-2">{right}</div>}
    </header>
  );
}
